<template>
  <div v-if="visivel" class="cfi-overlay" @click.self="$emit('fechar')">
    <div class="cfi-modal">

      <!-- Cabeçalho -->
      <div class="cfi-modal__header">
        <div>
          <h4 class="cfi-modal__title">
            <i class="fas fa-tools mr-2"></i>Corrigir Dados Fiscais do Item
          </h4>
          <small class="cfi-modal__subtitle" v-if="item">
            {{ item.descricao }}
          </small>
        </div>
        <button class="cfi-btn cfi-btn--light" @click="$emit('fechar')">✕</button>
      </div>

      <!-- Corpo -->
      <div class="cfi-modal__body">

        <!-- Alerta com campo faltante -->
        <div v-if="erroInfo" class="cfi-alert cfi-alert--danger">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <span>
            Campo obrigatório ausente: <strong>{{ erroInfo.campo }}</strong>
          </span>
        </div>

        <!-- Card com dados do item -->
        <div v-if="item" class="cfi-item-card">
          <div class="cfi-item-card__title">
            <i class="fas fa-box mr-1"></i>
            <strong>{{ item.descricao }}</strong>
          </div>
          <div class="cfi-item-card__sub">
            Qtd: {{ item.quantidade }} · Unitário: R$ {{ fmtBRL(item.valor_unitario) }} · Total: R$ {{ fmtBRL(item.total) }}
          </div>
        </div>

        <!-- Formulário fiscal -->
        <div class="cfi-fiscal-form">

          <!-- NCM -->
          <div class="cfi-form-group">
            <label class="cfi-label">
              NCM
              <span v-if="erroInfo && erroInfo.campo === 'NCM'" class="cfi-badge cfi-badge--danger ml-1">campo faltante</span>
            </label>
            <input
              ref="ncmInput"
              v-model="form.ncm"
              type="text"
              class="cfi-input"
              :class="{ 'cfi-input--erro': erros.ncm }"
              maxlength="10"
              placeholder="ex: 22021000  (8 dígitos)"
              @input="erros.ncm = null"
            />
            <small v-if="erros.ncm" class="cfi-erro-msg">{{ erros.ncm }}</small>
            <small class="cfi-hint">Nomenclatura Comum do Mercosul — 8 dígitos obrigatórios.</small>
          </div>

          <!-- CFOP -->
          <div class="cfi-form-group">
            <label class="cfi-label">
              CFOP
              <span v-if="erroInfo && erroInfo.campo === 'CFOP'" class="cfi-badge cfi-badge--danger ml-1">campo faltante</span>
            </label>
            <input
              v-model="form.cfop"
              type="text"
              class="cfi-input"
              :class="{ 'cfi-input--erro': erros.cfop }"
              maxlength="5"
              placeholder="ex: 5102  (4 dígitos)"
              @input="erros.cfop = null"
            />
            <small v-if="erros.cfop" class="cfi-erro-msg">{{ erros.cfop }}</small>
            <small class="cfi-hint">Para NFC-e de venda interna, use <strong>5102</strong>.</small>
          </div>

          <!-- CSOSN (Simples Nacional) -->
          <div class="cfi-form-group">
            <label class="cfi-label">
              CSOSN
              <span v-if="erroInfo && (erroInfo.campo === 'CSOSN' || erroInfo.campo === 'CST')" class="cfi-badge cfi-badge--danger ml-1">campo faltante</span>
            </label>
            <select v-model="form.csosn" class="cfi-select" :class="{ 'cfi-input--erro': erros.csosn }">
              <option value="">— selecione —</option>
              <option value="102">102 – Tributada pelo Simples Nacional sem permissão de crédito</option>
              <option value="103">103 – Isenção para faixa de receita bruta</option>
              <option value="300">300 – Imune</option>
              <option value="400">400 – Não tributada pelo Simples Nacional</option>
              <option value="500">500 – ICMS cobrado por substituição tributária</option>
              <option value="900">900 – Outros (Regime Normal)</option>
            </select>
            <small v-if="erros.csosn" class="cfi-erro-msg">{{ erros.csosn }}</small>
            <small class="cfi-hint">Para NFC-e Simples Nacional sem crédito, use <strong>102</strong>.</small>
          </div>

          <!-- CST ICMS (Regime Normal) -->
          <div class="cfi-form-group">
            <label class="cfi-label">CST ICMS <small class="text-muted">(Regime Normal)</small></label>
            <select v-model="form.cst_icms" class="cfi-select">
              <option value="">— não se aplica / Simples Nacional —</option>
              <option value="00">00 – Tributada integralmente</option>
              <option value="20">20 – Com redução de BC</option>
              <option value="40">40 – Isenta</option>
              <option value="41">41 – Não tributada</option>
              <option value="50">50 – Suspensão</option>
              <option value="60">60 – ICMS cobrado anteriormente por ST</option>
            </select>
            <small class="cfi-hint">Preencha somente se o emitente é Regime Normal (CRT 3).</small>
          </div>

          <!-- Origem -->
          <div class="cfi-form-group">
            <label class="cfi-label">Origem da Mercadoria</label>
            <select v-model="form.origem" class="cfi-select">
              <option value="0">0 – Nacional</option>
              <option value="1">1 – Estrangeira – importação direta</option>
              <option value="2">2 – Estrangeira – adquirida no mercado interno</option>
              <option value="3">3 – Nacional com conteúdo externo &gt; 40%</option>
              <option value="4">4 – Nacional – processo produtivo básico</option>
              <option value="5">5 – Nacional – conteúdo externo ≤ 40%</option>
              <option value="6">6 – Estrangeira – importação direta (sem similar nacional)</option>
              <option value="7">7 – Estrangeira – adquirida no mercado (sem similar nacional)</option>
              <option value="8">8 – Nacional – conteúdo externo &gt; 70%</option>
            </select>
          </div>

          <!-- CST PIS -->
          <div class="cfi-form-group">
            <label class="cfi-label">CST PIS</label>
            <select v-model="form.cst_pis" class="cfi-select">
              <option value="07">07 – Operação Isenta da Contribuição</option>
              <option value="08">08 – Operação sem Incidência da Contribuição</option>
              <option value="49">49 – Outras Operações de Saída</option>
              <option value="99">99 – Outras Operações</option>
            </select>
          </div>

          <!-- CST COFINS -->
          <div class="cfi-form-group">
            <label class="cfi-label">CST COFINS</label>
            <select v-model="form.cst_cofins" class="cfi-select">
              <option value="07">07 – Operação Isenta da Contribuição</option>
              <option value="08">08 – Operação sem Incidência da Contribuição</option>
              <option value="49">49 – Outras Operações de Saída</option>
              <option value="99">99 – Outras Operações</option>
            </select>
          </div>

        </div><!-- /cfi-fiscal-form -->

        <!-- Erro geral -->
        <div v-if="erroGeral" class="cfi-alert cfi-alert--danger mt-2">
          <i class="fas fa-exclamation-circle mr-1"></i>{{ erroGeral }}
        </div>

      </div><!-- /cfi-modal__body -->

      <!-- Rodapé -->
      <div class="cfi-modal__footer">
        <button class="cfi-btn cfi-btn--secondary" @click="$emit('fechar')">
          Cancelar
        </button>
        <button class="cfi-btn cfi-btn--primary" @click="salvar">
          <i class="fas fa-check mr-1"></i>Salvar e Aplicar
        </button>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: "CorrecaoFiscalItemCarrinho",

  props: {
    visivel: { type: Boolean, default: false },
    /** Item do carrinho (in-memory) */
    item:    { type: Object, default: null },
    /** Resultado de parseErroFiscalProduto – pode ser null para abertura manual */
    erroInfo: { type: Object, default: null },
  },

  emits: ["fechar", "salvar"],

  data() {
    return {
      form: {
        ncm:        "",
        cfop:       "",
        csosn:      "102",
        cst_icms:   "",
        origem:     "0",
        cst_pis:    "07",
        cst_cofins: "07",
      },
      erros: {
        ncm:   null,
        cfop:  null,
        csosn: null,
      },
      erroGeral: null,
    };
  },

  watch: {
    // Preenche formulário sempre que o item mudar
    item: {
      immediate: true,
      handler(novo) {
        if (!novo) return;
        this._preencherFormComItem(novo);
      },
    },

    visivel(val) {
      if (val) {
        this.erroGeral = null;
        this.erros = { ncm: null, cfop: null, csosn: null };
        this.$nextTick(() => {
          this.$refs.ncmInput && this.$refs.ncmInput.focus();
        });
      }
    },
  },

  methods: {
    fmtBRL(v) {
      return Number(v || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    _preencherFormComItem(item) {
      const orig = item.produto_original || {};

      // Lê dados fiscais do item ou do produto original
      let payloadFiscal = {};
      try {
        const p = item.payload_json ? JSON.parse(item.payload_json) : {};
        const prod = p.produto || p;
        payloadFiscal = prod.fiscal || prod._fiscal || prod.dados_fiscais || prod;
      } catch (_) { /* ignore */ }

      this.form.ncm   = item.ncm_codigo   || orig.ncm_codigo   || payloadFiscal.ncm   || "";
      this.form.cfop  = item.cfop_codigo  || orig.cfop_codigo  || payloadFiscal.cfop  || payloadFiscal.cfop_nfce || "";

      // CSOSN – produto pode ter cst_csosn_codigo (simples) ou contribuinte (normal)
      this.form.csosn    = item.cst_csosn_codigo              || orig.cst_csosn_codigo              || payloadFiscal.csosn || "102";
      this.form.cst_icms = item.cst_csosn_contribuinte_codigo || orig.cst_csosn_contribuinte_codigo || payloadFiscal.cst   || "";
      this.form.origem   = String(payloadFiscal.orig ?? orig.origem_mercadoria ?? "0");

      this.form.cst_pis    = item.cst_pis_codigo    || orig.cst_pis_codigo    || payloadFiscal.cst_pis    || "07";
      this.form.cst_cofins = item.cst_cofins_codigo || orig.cst_cofins_codigo || payloadFiscal.cst_cofins || "07";
    },

    _validar() {
      let ok = true;
      this.erros = { ncm: null, cfop: null, csosn: null };
      this.erroGeral = null;

      // NCM: obrigatório, 8 dígitos numéricos
      const ncmLimpo = String(this.form.ncm || "").replace(/\D/g, "");
      if (!ncmLimpo) {
        this.erros.ncm = "NCM é obrigatório.";
        ok = false;
      } else if (ncmLimpo.length !== 8) {
        this.erros.ncm = `NCM deve ter 8 dígitos (informado: ${ncmLimpo.length}).`;
        ok = false;
      }

      // CFOP: obrigatório, 4 dígitos
      const cfopLimpo = String(this.form.cfop || "").replace(/\D/g, "");
      if (!cfopLimpo) {
        this.erros.cfop = "CFOP é obrigatório.";
        ok = false;
      } else if (cfopLimpo.length !== 4) {
        this.erros.cfop = `CFOP deve ter 4 dígitos (informado: ${cfopLimpo.length}).`;
        ok = false;
      }

      // CSOSN/CST: ao menos um preenchido
      if (!this.form.csosn && !this.form.cst_icms) {
        this.erros.csosn = "Informe o CSOSN (Simples Nacional) ou o CST ICMS (Regime Normal).";
        ok = false;
      }

      return ok;
    },

    salvar() {
      if (!this._validar()) return;

      const ncmLimpo  = String(this.form.ncm  || "").replace(/\D/g, "");
      const cfopLimpo = String(this.form.cfop || "").replace(/\D/g, "");

      this.$emit("salvar", {
        ncm:        ncmLimpo,
        cfop:       cfopLimpo,
        csosn:      this.form.csosn    || null,
        cst_icms:   this.form.cst_icms || null,
        origem:     this.form.origem   || "0",
        cst_pis:    this.form.cst_pis    || "07",
        cst_cofins: this.form.cst_cofins || "07",
      });
    },
  },
};
</script>

<style scoped>
.cfi-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.cfi-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.22);
  width: 540px;
  max-width: 96vw;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cfi-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.1rem 1.4rem 0.9rem;
  border-bottom: 1px solid #dee2e6;
  background: #fffbeb;
}

.cfi-modal__title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 2px;
  color: #212529;
}

.cfi-modal__subtitle {
  color: #6c757d;
  font-size: 0.8rem;
}

.cfi-modal__body {
  padding: 1.1rem 1.4rem;
  overflow-y: auto;
  flex: 1;
}

.cfi-modal__footer {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-end;
  padding: 0.85rem 1.4rem;
  border-top: 1px solid #dee2e6;
}

/* Alerts */
.cfi-alert {
  padding: 0.6rem 0.9rem;
  border-radius: 7px;
  font-size: 0.85rem;
  margin-bottom: 0.8rem;
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
}
.cfi-alert--danger  { background: #fff5f5; border: 1px solid #fecaca; color: #7f1d1d; }

/* Item card */
.cfi-item-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
  margin-bottom: 1rem;
}
.cfi-item-card__title { font-weight: 600; font-size: 0.9rem; color: #212529; margin-bottom: 0.2rem; }
.cfi-item-card__sub   { font-size: 0.78rem; color: #6c757d; }

/* Form */
.cfi-fiscal-form { display: flex; flex-direction: column; gap: 0.7rem; }
.cfi-form-group  { display: flex; flex-direction: column; gap: 0.25rem; }
.cfi-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
}
.cfi-input, .cfi-select {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  font-size: 0.875rem;
  color: #212529;
  background: #fff;
  width: 100%;
}
.cfi-input--erro { border-color: #ef4444 !important; }
.cfi-input:focus, .cfi-select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}
.cfi-hint     { font-size: 0.72rem; color: #6c757d; }
.cfi-erro-msg { font-size: 0.72rem; color: #dc2626; }

/* Badges */
.cfi-badge { font-size: 0.65rem; padding: 1px 6px; border-radius: 4px; }
.cfi-badge--danger { background: #fee2e2; color: #b91c1c; }

/* Buttons */
.cfi-btn {
  padding: 0.45rem 1rem;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: background 0.15s;
}
.cfi-btn--primary   { background: #6366f1; color: #fff; }
.cfi-btn--primary:hover { background: #4f46e5; }
.cfi-btn--secondary { background: #e5e7eb; color: #374151; }
.cfi-btn--secondary:hover { background: #d1d5db; }
.cfi-btn--light { background: transparent; color: #6c757d; font-size: 1rem; padding: 0.2rem 0.5rem; }
.cfi-btn--light:hover { background: #f3f4f6; }

.mt-2 { margin-top: 0.5rem; }
.ml-1 { margin-left: 0.25rem; }
.mr-1 { margin-right: 0.25rem; }
.mr-2 { margin-right: 0.5rem; }
.text-muted { color: #6c757d; }
</style>
